import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-500 text-lg">文章未找到</p>
            <Link
              to="/articles"
              className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm font-medium cursor-pointer"
            >
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: '美丽资讯', path: '/articles' },
    { name: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '女大学生的沙龙室',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <Breadcrumb items={breadcrumbItems} />

        <article className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className="text-xs text-accent-600 bg-accent-100 px-2.5 py-1 rounded-full">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">
                  {article.readTime}
                </span>
                <span className="text-xs text-foreground-400">
                  {article.date}
                </span>
              </div>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 leading-snug">
                {article.title}
              </h1>
              <div className="mt-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 font-heading font-bold text-xs">
                  {article.author[0]}
                </div>
                <span className="text-sm text-foreground-600">
                  {article.author}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-8 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs text-foreground-600 bg-background-100 px-3 py-1 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                [&_h3]:text-lg [&_h3]:font-heading [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_strong]:text-foreground-900 [&_strong]:font-semibold
                [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ul]:space-y-1
                [&_li]:text-foreground-700 [&_li]:text-sm [&_li]:leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200/70">
              <Link
                to="/articles"
                className="inline-flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />
                返回文章列表
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}